<script lang="ts">
  import {
    FileDropzone,
    getModalStore,
    getToastStore,
    InputChip,
    popup,
    ProgressRadial,
  } from '@skeletonlabs/skeleton'
  import type { PopupSettings } from '@skeletonlabs/skeleton'
  import camelcaseKeys from 'camelcase-keys'
  import { onMount, type SvelteComponent } from 'svelte'

  import { DatasetMetadataType } from './dataset-metadata-type'
  import {
    allModalityTypes,
    allowedLabelingInfoByModalityType,
    COCO,
    datasetMetadataTypeValuesByLabelingType,
    displayAugmentationNames,
    displayLabelingTypes,
    displayMetadataTypes,
    displayModalityTypes,
    displaySTTLanguages,
    HuggingFaceAudio,
    type IDataset,
    ImageAugmentationName,
    imageAugmentationValues,
    isCOCO,
    isHirundoCSV,
    isHuggingFaceAudio,
    isMultimodalHirundoCSV,
    isMultimodalTabularLikeModality,
    isYOLO,
    LabelingType,
    labelingTypesValuesByModalityType,
    ModalityType,
    modalityTypesWithData,
    multimodalChildModalities,
    MultimodalHirundoCSV,
    MultimodalModalityCSV,
    MultimodalModalityType,
    parseClasses,
    parseLabelingInfo,
    productToSelectableModalityTypes,
    runQA,
    sttLanguages,
    TabAugmentationName,
    tabAugmentationValues,
    YOLO,
  } from './datasets-store.svelte'
  import CloseModalButton from '@components/CloseModalButton.svelte'
  import MetadataFields from '@components/MetadataFields.svelte'
  import ReadOnly from '@components/ReadOnly.svelte'
  import RequiredNameField from '@components/RequiredNameField.svelte'
  import Select from '@components/Select.svelte'
  import StorageConfigurator from '@components/StorageConfigurator.svelte'
  import {
    organizationAsyncState,
    organizationsStore,
  } from '@routes/organizations/organizations-store'
  import {
    createStorageConfig,
    deleteStorageConfig,
    getStoragePrefix,
    type IStorageConfig,
    prepareInputValuesForQuery,
    type StorageType,
  } from '@routes/storage-configs/storage-configs-store'
  import { localStoragePrefixes } from '@src/stores/local-storage.svelte'
  import { loggedInStore, User } from '@stores/logged-in-store'
  import { type Config, getConfig } from '@utils/config.svelte'
  import { HirundoCSV } from '@utils/hirundo-csv.svelte'
  import { hotReloadState } from '@utils/hot-reload'
  import { ClassesOption } from '@utils/misc'
  import { setRestoreState } from '@utils/modal-restore'
  import { Product, unlearningProducts, type UnlearningProducts } from '@utils/product'
  import { ValidityState } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'
  import NounModalityData from '$lib/components/icons/NounModalityData.svelte'
  import NounModalityMultimodal from '$lib/components/icons/NounModalityMultimodal.svelte'
  import NounModalityRadar from '$lib/components/icons/NounModalityRadar.svelte'
  import NounModalityTimeseries from '$lib/components/icons/NounModalityTimeseries.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()
  const readOnly = $modalStore[0]?.meta?.readOnly || false
  const toastStore = getToastStore()
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey
  type TabularColumnMode = 'featureCols' | 'extraNonFeatureCols'
  type AugmentationSelection = ImageAugmentationName[] | TabAugmentationName[]

  interface DatasetModalRestoreState {
    datasetInputValues: DatasetInput & {
      featureCols?: string[]
      extraNonFeatureCols?: string[]
    }
    fileImportMode: boolean
    isAccordionOpen: boolean
    selectedClassesOption: ClassesOption
    storageInputValues: Record<string, unknown>
    selectedStorageType: StorageType | null
    storageConfigName: string
    tabularColumnMode?: TabularColumnMode
  }
  const isDatasetModalRestoreState = (v: unknown): v is DatasetModalRestoreState =>
    !!v && typeof v === 'object' && 'datasetInputValues' in (v as Record<string, unknown>)

  const normalizeDatasetInputValues = (
    inputValues: DatasetModalRestoreState['datasetInputValues'],
  ): DatasetInput => ({
    ...inputValues,
    featureCols: inputValues.featureCols ?? [],
    extraNonFeatureCols: inputValues.extraNonFeatureCols ?? [],
  })

  const currentUser: User = $loggedInStore

  let dataset: IDataset | null = $modalStore[0].value
  const action = $derived(dataset && !$modalStore[0].meta?.duplicate ? 'update' : 'create')
  let currentOrganizationId = dataset?.organizationId ?? currentUser.primaryOrganizationId
  let selectedStorageConfig: IStorageConfig | null = $state(null) // For selected storage in search input
  let datasetFormElement: HTMLFormElement | undefined = $state()
  let fileImportMode = $state(false)
  type DatasetInput = Partial<Pick<IDataset, 'modality' | 'name' | 'language' | 'organizationId'>> &
    Pick<IDataset, 'dataRootUrl' | 'labelingInfo'> & {
      labelingType: LabelingType | null
      classes: string[]
      featureCols: string[]
      extraNonFeatureCols: string[]
      selectedStorageConfigId?: number | null
      selectedStorageConfigName: string
      augmentations: AugmentationSelection | null
    }

  let datasetInputValues: DatasetInput = $state({
    name: '',
    organizationId: currentOrganizationId,
    selectedStorageConfigName: '',
    dataRootUrl: '',
    labelingType: null,
    labelingInfo: new HirundoCSV(),
    classes: [],
    featureCols: [],
    extraNonFeatureCols: [],
    augmentations: null,
    creatingDataset: true,
  })

  const augmentationValues = $derived(
    datasetInputValues.modality === ModalityType.TABULAR ||
      datasetInputValues.modality === ModalityType.TIMESERIES
      ? tabAugmentationValues
      : imageAugmentationValues,
  )
  const isTabularLikeDataset = $derived(
    datasetInputValues.modality === ModalityType.TABULAR ||
      datasetInputValues.modality === ModalityType.TIMESERIES,
  )
  const canOmitDataRootUrl = $derived(
    datasetInputValues.modality === ModalityType.TABULAR ||
      datasetInputValues.modality === ModalityType.TIMESERIES,
  )

  let invalids = $state({
    name: new ValidityState(),
    selectedStorageConfigName: new ValidityState(),
    labelingType: new ValidityState(),
    csvUrl: new ValidityState(),
    jsonUrl: new ValidityState(),
    dataRootUrl: new ValidityState(),
    dataYamlUrl: new ValidityState(),
    labelsDirUrl: new ValidityState(),
  })

  let datasetMetadataOptions = $derived.by(() => {
    if (!datasetInputValues.modality) {
      return []
    }
    let derivedDatasetMetadataOptions: DatasetMetadataType[] | null = null
    if (datasetInputValues.labelingType) {
      for (const [modality, allowedLabelingTypes] of Object.entries(
        allowedLabelingInfoByModalityType,
      )) {
        if (modality === datasetInputValues.modality) {
          derivedDatasetMetadataOptions = datasetMetadataTypeValuesByLabelingType[
            datasetInputValues.labelingType
          ].filter((type) => allowedLabelingTypes.includes(type))
          break
        }
      }
      if (!derivedDatasetMetadataOptions) {
        throw new Error(
          `No allowed labeling types found for modality ${datasetInputValues.modality}`,
        )
      }
    } else {
      derivedDatasetMetadataOptions = [
        ...new Set(Object.values(datasetMetadataTypeValuesByLabelingType).flat()),
      ]
    }

    return derivedDatasetMetadataOptions
  })

  $effect(() => {
    if (
      datasetInputValues.labelingInfo.type &&
      datasetMetadataOptions?.length &&
      ![...datasetMetadataOptions].includes(datasetInputValues.labelingInfo.type)
    ) {
      datasetInputValues.labelingInfo =
        datasetInputValues.modality === ModalityType.MULTIMODAL
          ? new MultimodalHirundoCSV()
          : new HirundoCSV()
      // Reset the metadata type to the first option
      // if the current one is not valid for the new labeling type
    }
  })

  let selectedClassesOption: ClassesOption = $state(ClassesOption.AutoDetection)
  let isAccordionOpen = $state(false)
  let isEditMode = $state(false)
  let isLoading = $state(false)
  let tabularColumnMode: TabularColumnMode = $state('extraNonFeatureCols')
  const tabularColumnOptions = [
    {
      mode: 'extraNonFeatureCols',
      label: 'Metadata / ID columns',
      info: 'Preserve these in QA results and exclude them from model features.',
      popupTarget: 'extraNonFeatureColsInfo',
    },
    {
      mode: 'featureCols',
      label: 'Feature columns',
      info: 'Use these as the complete tabular model feature set.',
      popupTarget: 'featureColsInfo',
    },
  ] satisfies {
    mode: TabularColumnMode
    label: string
    info: string
    popupTarget: string
  }[]
  const getTabularColumnInfoPopup = (target: string): PopupSettings => ({
    event: 'hover',
    target,
    placement: 'top',
  })

  let storageConfigurator: StorageConfigurator | undefined = $state()

  // Values for the storageFormFields component
  let storageFormElement: HTMLFormElement | undefined = $state()
  const initialStorageConfig = $state.snapshot(dataset?.storageConfig)
  let storageConfig = $state(initialStorageConfig)
  let selectedStorageType = $state(initialStorageConfig?.type || null)
  let storageConfigName = $state(initialStorageConfig?.name || '')
  let storageInputValues = $state({}) // TODO: type more strictly
  let config: Config | null = $state(null)
  const modalityToMultimodalModalityType = {
    [ModalityType.VISION]: MultimodalModalityType.VISION,
    [ModalityType.RADAR]: MultimodalModalityType.RADAR,
    [ModalityType.TABULAR]: MultimodalModalityType.TABULAR,
    [ModalityType.TIMESERIES]: MultimodalModalityType.TIMESERIES,
  } satisfies Partial<Record<ModalityType, MultimodalModalityType>>

  onMount(async () => {
    await initializeInputValues(dataset)
    config = await getConfig()

    // Restore previously cached state if present
    if (
      $modalStore[0]?.meta?.restoreState &&
      isDatasetModalRestoreState($modalStore[0].meta.restoreState)
    ) {
      const restoreState = $modalStore[0].meta.restoreState
      datasetInputValues = normalizeDatasetInputValues(restoreState.datasetInputValues)
      fileImportMode = restoreState.fileImportMode
      isAccordionOpen = restoreState.isAccordionOpen
      selectedClassesOption = restoreState.selectedClassesOption
      storageInputValues = restoreState.storageInputValues
      selectedStorageType = restoreState.selectedStorageType
      storageConfigName = restoreState.storageConfigName
      tabularColumnMode =
        restoreState.tabularColumnMode ??
        (datasetInputValues.featureCols.length ? 'featureCols' : 'extraNonFeatureCols')
    }
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    setRestoreState(restoreKey, {
      datasetInputValues,
      fileImportMode,
      isAccordionOpen,
      selectedClassesOption,
      storageInputValues,
      selectedStorageType,
      storageConfigName,
      tabularColumnMode,
    } satisfies DatasetModalRestoreState)
  })

  const modalityTypes = $derived.by(() => {
    if (!config) {
      return []
    }
    const activeModalities =
      config.activeProducts
        ?.filter((product) => {
          return (
            !unlearningProducts.includes(product as Product) &&
            product !== Product.LLM_BEHAVIOR_EVAL
          )
        })
        ?.flatMap(
          (product) =>
            productToSelectableModalityTypes[
              product as Exclude<Product, UnlearningProducts | Product.LLM_BEHAVIOR_EVAL>
            ],
        ) || allModalityTypes
    const activeMultimodalSourceCount = activeModalities.filter((modality) =>
      multimodalChildModalities.includes(modality),
    ).length
    if (activeModalities.includes(ModalityType.MULTIMODAL)) {
      return activeMultimodalSourceCount >= 2
        ? activeModalities
        : activeModalities.filter((modality) => modality !== ModalityType.MULTIMODAL)
    }
    return activeModalities
  })

  const availableMultimodalModalityOptions = $derived(
    modalityTypes
      .filter((modality) => multimodalChildModalities.includes(modality))
      .map(
        (modality) =>
          modalityToMultimodalModalityType[
            modality as keyof typeof modalityToMultimodalModalityType
          ],
      )
      .filter((modality): modality is MultimodalModalityType => modality !== undefined),
  )

  const initializeInputValues = async (dataset: IDataset | null) => {
    if (!dataset) {
      return
    }

    selectedStorageConfig = dataset.storageConfig
    isEditMode = !$modalStore[0].meta?.duplicate // Disables the accordion and search input unless duplicating
    if ((dataset.classes?.length ?? 0) > 0) {
      selectedClassesOption = ClassesOption.ManualInput
    }

    const {
      name,
      modality,
      labelingType,
      language,
      organizationId,
      dataRootUrl,
      labelingInfo,
      classes = [],
      featureCols = [],
      extraNonFeatureCols = [],
      augmentations,
    } = dataset

    const newName = $modalStore[0].meta.newName

    datasetInputValues = {
      name: newName ? newName : name,
      modality,
      labelingType,
      language,
      organizationId,
      selectedStorageConfigId: selectedStorageConfig?.id,
      selectedStorageConfigName: selectedStorageConfig?.name,
      dataRootUrl,
      labelingInfo,
      classes: classes || [],
      featureCols: featureCols || [],
      extraNonFeatureCols: extraNonFeatureCols || [],
      augmentations,
    }
    tabularColumnMode = featureCols?.length ? 'featureCols' : 'extraNonFeatureCols'
  }

  const submitStorageConfigFields = async () => {
    if (!storageFormElement?.reportValidity()) {
      return
    }
    if (!selectedStorageType) {
      console.error('No storage type selected')
      return
    }

    const storageConfigExtra = prepareInputValuesForQuery(storageInputValues, selectedStorageType)
    const storageConfigPayload = {
      ...storageConfigExtra,
      organizationId: currentOrganizationId,
      name: storageConfigName,
      type: selectedStorageType,
    }

    const newStorageConfig = await createStorageConfig(
      storageConfigPayload,
      toastStore,
      currentUser.userId,
      currentUser.fullName,
    )
    if (!newStorageConfig) {
      console.error('Failed to create new storage config')
      return
    }
    return newStorageConfig
  }

  const createOrUpdateDataset = async () => {
    let createdStorageConfig = false
    try {
      if (isAccordionOpen) {
        const storageConfigCreated = await submitStorageConfigFields()
        if (storageConfigCreated) {
          createdStorageConfig = true
        } else {
          console.error(`Failed to ${action} new storage config from dataset form`)
          throw new Error(`Failed to ${action} new storage config from dataset form`)
        }
        datasetInputValues.selectedStorageConfigId = storageConfigCreated.id
      }

      if (!datasetInputValues.labelingType) {
        invalids.labelingType.setInvalid('Labeling type is required')
        throw new Error('Labeling type is required')
      }
      const computedDataRootUrl =
        datasetInputValues.dataRootUrl && datasetInputValues.dataRootUrl.trim() !== ''
          ? datasetInputValues.dataRootUrl
          : canOmitDataRootUrl
            ? null
            : dataPrefix
      const payload = {
        name: datasetInputValues.name,
        modality: datasetInputValues.modality,
        organizationId: datasetInputValues.organizationId,
        labelingType: datasetInputValues.labelingType,
        storageConfigId: datasetInputValues.selectedStorageConfigId,
        ...(computedDataRootUrl ? { dataRootUrl: computedDataRootUrl } : {}),
        labelingInfo: $state.snapshot(datasetInputValues.labelingInfo),
        classes: datasetInputValues.classes,
        featureCols: isTabularLikeDataset
          ? tabularColumnMode === 'featureCols'
            ? datasetInputValues.featureCols
            : []
          : null,
        extraNonFeatureCols: isTabularLikeDataset
          ? tabularColumnMode === 'extraNonFeatureCols'
            ? datasetInputValues.extraNonFeatureCols
            : []
          : null,
        ...(datasetInputValues.labelingType === LabelingType.SpeechToText
          ? { language: datasetInputValues.language }
          : {
              augmentations:
                datasetInputValues.modality === ModalityType.MULTIMODAL
                  ? null
                  : datasetInputValues.augmentations,
            }),
      }

      if (!$modalStore[0].response) {
        console.error("Modal doesn't have a response function")
        throw new Error("Modal doesn't have a response function")
      }
      await $modalStore[0].response({ payload })
      return payload
    } catch (error) {
      if (createdStorageConfig && datasetInputValues.selectedStorageConfigId) {
        await deleteStorageConfig(datasetInputValues.selectedStorageConfigId)
        // ⬆️ This cleans up any storage config that was created, enabling the user to retry
      }
      console.error('Failed to create dataset')
      throw error
    }
  }

  const createOrUpdateHandler = async () => {
    if (!datasetFormElement?.reportValidity()) {
      return
    }
    try {
      isLoading = true
      await createOrUpdateDataset()
      parent.onClose()
    } finally {
      isLoading = false
    }
  }

  const createOrUpdateAndRunHandler = async () => {
    if (!datasetFormElement?.reportValidity()) {
      return
    }
    try {
      isLoading = true
      const dataset = await createOrUpdateDataset()
      const datasetId = $modalStore[0].meta.datasetId
      if (!datasetId) {
        console.error(`Failed to ${action} dataset. Could not run dataset QA`)
        isLoading = false
        return
      }
      await runQA(dataset, datasetId, toastStore, modalStore)
      parent.onClose()
    } finally {
      isLoading = false
    }
  }

  const dataPrefix = $derived.by(() =>
    getStoragePrefix({
      storageInputValues,
      selectedStorageConfig,
      type: selectedStorageType ?? selectedStorageConfig?.type,
      localStoragePrefix: localStoragePrefixes.datasetUrlPrefix,
    }),
  )

  const importFromFiles = () => {
    fileImportMode = true
  }

  const removeMultimodalCsv = (indexToRemove: number) => {
    if (!isMultimodalHirundoCSV(datasetInputValues.labelingInfo)) {
      return
    }
    datasetInputValues.labelingInfo.modalityCsvs =
      datasetInputValues.labelingInfo.modalityCsvs.filter(
        (_entry, index) => index !== indexToRemove,
      )
  }

  const addMultimodalCsv = () => {
    if (!isMultimodalHirundoCSV(datasetInputValues.labelingInfo)) {
      return
    }
    const selectedModalities = new Set(
      datasetInputValues.labelingInfo.modalityCsvs.map((modalityCsv) => modalityCsv.modality),
    )
    const nextModality = availableMultimodalModalityOptions.find(
      (modality) => !selectedModalities.has(modality),
    )
    if (!nextModality) {
      return
    }
    datasetInputValues.labelingInfo.modalityCsvs = [
      ...datasetInputValues.labelingInfo.modalityCsvs,
      new MultimodalModalityCSV(nextModality),
    ]
  }

  const multimodalModalityName = (modality: MultimodalModalityType) => {
    switch (modality) {
      case MultimodalModalityType.VISION:
        return 'Vision'
      case MultimodalModalityType.RADAR:
        return 'Radar'
      case MultimodalModalityType.TABULAR:
        return 'Tabular'
      case MultimodalModalityType.TIMESERIES:
        return 'Time series'
      default:
        return modality
    }
  }

  const isMultimodalVisionLikeModality = (modality: MultimodalModalityType) =>
    [MultimodalModalityType.VISION, MultimodalModalityType.RADAR].includes(modality)

  const multimodalAugmentationValues = (modality: MultimodalModalityType) =>
    isMultimodalTabularLikeModality(modality) ? tabAugmentationValues : imageAugmentationValues

  const resetMultimodalModalityFields = (modalityCsv: MultimodalModalityCSV) => {
    modalityCsv.augmentations = null
    if (!isMultimodalVisionLikeModality(modalityCsv.modality)) {
      modalityCsv.dataRootUrl = null
    }
    if (!isMultimodalTabularLikeModality(modalityCsv.modality)) {
      modalityCsv.featureCols = null
      modalityCsv.extraNonFeatureCols = null
    } else if (modalityCsv.featureCols === null && modalityCsv.extraNonFeatureCols === null) {
      modalityCsv.extraNonFeatureCols = []
    }
  }

  const handleFileImport = (event: Event & { currentTarget: HTMLInputElement }) => {
    const file = event.currentTarget.files?.[0]
    if (file) {
      if (file.type !== 'application/json') {
        console.error('File is not a JSON file')
        toastStore.trigger({
          message:
            'File is not a JSON file. Please make sure that you are uploading the correct file.',
          background: 'variant-filled-error',
        })
        return
      }
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const json = camelcaseKeys(JSON.parse(event.target?.result as string), { deep: true })
          // Note: the cast to string is safe since we are using `reader.readAsText`
          datasetInputValues.name = json.name
          datasetInputValues.labelingType =
            LabelingType[json.labelingType as keyof typeof LabelingType]
          if (datasetInputValues.labelingType === LabelingType.SpeechToText) {
            datasetInputValues.language = json.language
          }
          datasetInputValues.classes = parseClasses(json.classes) ?? []
          if (!storageConfigurator) {
            console.error('Storage configurator not found', storageConfigurator)
            throw new Error('Storage configurator not found')
          }
          storageConfigurator.setStorageConfig(json.storageConfig.name)
          datasetInputValues.dataRootUrl = json.dataRootUrl
          datasetInputValues.labelingInfo = parseLabelingInfo(
            camelcaseKeys(json, { deep: true }).labelingInfo,
          )
          datasetInputValues.organizationId = json.organizationId
          datasetInputValues.augmentations = json.augmentations
          // You can now use the parsed JSON as needed
          fileImportMode = false
        } catch (error) {
          console.error('Failed to parse file:', error)
          toastStore.trigger({
            message: 'Failed to parse JSON file',
            background: 'variant-filled-error',
          })
        }
      }
      reader.readAsText(file)
    }
  }

  const toggleAugmentationSelection = (
    augmentations: AugmentationSelection | null,
    augmentation: ImageAugmentationName | TabAugmentationName,
  ): AugmentationSelection => {
    const selectedAugmentations = augmentations as
      | (ImageAugmentationName | TabAugmentationName)[]
      | null
    if (selectedAugmentations?.includes(augmentation)) {
      return selectedAugmentations.filter(
        (selectedAugmentation) => selectedAugmentation !== augmentation,
      ) as AugmentationSelection
    }
    return [...(selectedAugmentations ?? []), augmentation] as AugmentationSelection
  }

  const toggleAugmentation = (augmentation: ImageAugmentationName | TabAugmentationName) => {
    datasetInputValues.augmentations = toggleAugmentationSelection(
      datasetInputValues.augmentations,
      augmentation,
    )
  }

  const toggleMultimodalAugmentation = (
    modalityCsv: MultimodalModalityCSV,
    augmentation: ImageAugmentationName | TabAugmentationName,
  ) => {
    modalityCsv.augmentations = toggleAugmentationSelection(modalityCsv.augmentations, augmentation)
  }

  const setTabularColumnMode = (mode: TabularColumnMode) => {
    tabularColumnMode = mode
    if (mode === 'featureCols') {
      datasetInputValues.extraNonFeatureCols = []
    } else {
      datasetInputValues.featureCols = []
    }
  }

  const multimodalTabularColumnMode = (modalityCsv: MultimodalModalityCSV): TabularColumnMode =>
    modalityCsv.featureCols !== null ? 'featureCols' : 'extraNonFeatureCols'

  const isMultimodalModalitySelectedElsewhere = (
    modality: MultimodalModalityType,
    currentIndex: number,
  ) =>
    isMultimodalHirundoCSV(datasetInputValues.labelingInfo) &&
    datasetInputValues.labelingInfo.modalityCsvs.some(
      (modalityCsv, index) => index !== currentIndex && modalityCsv.modality === modality,
    )

  const hasAvailableMultimodalModality = $derived(
    isMultimodalHirundoCSV(datasetInputValues.labelingInfo) &&
      datasetInputValues.labelingInfo.modalityCsvs.length <
        availableMultimodalModalityOptions.length,
  )

  const setMultimodalTabularColumnMode = (
    modalityCsv: MultimodalModalityCSV,
    mode: TabularColumnMode,
  ) => {
    if (mode === 'featureCols') {
      modalityCsv.featureCols = modalityCsv.featureCols ?? []
      modalityCsv.extraNonFeatureCols = null
    } else {
      modalityCsv.extraNonFeatureCols = modalityCsv.extraNonFeatureCols ?? []
      modalityCsv.featureCols = null
    }
  }

  hotReloadState({
    name: 'datasetInputValues',
    getter: () => datasetInputValues,
    setter: (value) => {
      datasetInputValues = value
    },
  })
</script>

{#snippet fieldInfo(message: string, popupTarget: string)}
  <button
    type="button"
    tabindex="-1"
    class="[&>*]:pointer-events-none"
    aria-label="Field info"
    use:popup={getTabularColumnInfoPopup(popupTarget)}
  >
    <Icon
      class="inline-block align-text-bottom text-surface-500"
      icon="iconoir:help-circle"
      height="1rem"
      width="1rem"
    />
  </button>
  <span class="card variant-filled-tertiary z-50 max-w-80 p-2 text-sm" data-popup={popupTarget}>
    <span>{message}</span>
    <span class="variant-filled-tertiary arrow"></span>
  </span>
{/snippet}

{#snippet columnSelection(
  title: string,
  columnMode: TabularColumnMode,
  setColumnMode: (mode: TabularColumnMode) => void,
  featureCols: string[],
  extraNonFeatureCols: string[],
  setFeatureCols: (value: string[]) => void,
  setExtraNonFeatureCols: (value: string[]) => void,
  radioName: string,
  extraNonFeatureColsName: string,
  featureColsName: string,
  popupPrefix: string,
  titleClass = 'pb-2',
)}
  <p class={titleClass}>{title}</p>
  {#if readOnly}
    <div class="space-y-2">
      <ReadOnly
        value={featureCols.length
          ? `Feature columns: ${featureCols.join(', ')}`
          : 'Feature columns: None'}
      />
      <ReadOnly
        value={extraNonFeatureCols.length
          ? `Metadata / ID columns: ${extraNonFeatureCols.join(', ')}`
          : 'Metadata / ID columns: None'}
      />
    </div>
  {:else}
    <div class="space-y-3">
      <div class="flex flex-wrap gap-4">
        {#each tabularColumnOptions as option (option.mode)}
          <div class="flex items-center gap-1">
            <label class="flex items-center gap-2">
              <input
                class="radio"
                type="radio"
                name={radioName}
                checked={columnMode === option.mode}
                onchange={() => {
                  setColumnMode(option.mode)
                }}
              />
              <span>{option.label}</span>
            </label>
            {@render fieldInfo(option.info, `${popupPrefix}${option.popupTarget}`)}
          </div>
        {/each}
      </div>
      {#if columnMode === 'extraNonFeatureCols'}
        <InputChip
          bind:value={() => extraNonFeatureCols, setExtraNonFeatureCols}
          name={extraNonFeatureColsName}
          placeholder="Type a column name and hit Enter"
          allowUpperCase
        />
      {:else}
        <InputChip
          bind:value={() => featureCols, setFeatureCols}
          name={featureColsName}
          placeholder="Type a column name and hit Enter"
          allowUpperCase
        />
      {/if}
    </div>
  {/if}
{/snippet}

{#snippet tabularColumnSelection(
  radioName: string,
  extraNonFeatureColsName: string,
  featureColsName: string,
  popupPrefix: string,
)}
  {@render columnSelection(
    'Column selection:',
    tabularColumnMode,
    setTabularColumnMode,
    datasetInputValues.featureCols,
    datasetInputValues.extraNonFeatureCols,
    (value) => {
      datasetInputValues.featureCols = value
    },
    (value) => {
      datasetInputValues.extraNonFeatureCols = value
    },
    radioName,
    extraNonFeatureColsName,
    featureColsName,
    popupPrefix,
  )}
{/snippet}

{#snippet multimodalChildTabularColumnSelection(modalityCsv: MultimodalModalityCSV, index: number)}
  {@render columnSelection(
    `${multimodalModalityName(modalityCsv.modality)} column selection`,
    multimodalTabularColumnMode(modalityCsv),
    (mode) => setMultimodalTabularColumnMode(modalityCsv, mode),
    modalityCsv.featureCols ?? [],
    modalityCsv.extraNonFeatureCols ?? [],
    (value) => {
      modalityCsv.featureCols = value
    },
    (value) => {
      modalityCsv.extraNonFeatureCols = value
    },
    `multimodal-tabular-column-mode-${index}`,
    `multimodal-extra-non-feature-cols-${index}`,
    `multimodal-feature-cols-${index}`,
    `multimodal-${index}-`,
    'text-sm font-medium',
  )}
{/snippet}

{#snippet augmentationSelection(
  title: string,
  selectedAugmentations: ImageAugmentationName[] | TabAugmentationName[] | null,
  availableAugmentations: ImageAugmentationName[] | TabAugmentationName[],
  setAugmentations: (value: ImageAugmentationName[] | TabAugmentationName[] | null) => void,
  toggleSelectedAugmentation: (augmentation: ImageAugmentationName | TabAugmentationName) => void,
  radioName: string,
  defaultId: string,
  customId: string,
  chipListId: string,
  titleClass = '',
  radioContainerClass = 'flex items-center space-x-2',
  chipContainerClass = 'variant-ghost-surface flex flex-wrap items-center justify-center gap-4 py-2',
)}
  <p class={titleClass}>{title}</p>
  <div>
    {#if readOnly}
      <ReadOnly
        value={selectedAugmentations !== null
          ? selectedAugmentations.length !== 0
            ? selectedAugmentations
                .map((augmentation) => displayAugmentationNames[augmentation])
                .join(', ')
            : 'None'
          : 'Default augmentations'}
      />
    {:else}
      <div class={radioContainerClass}>
        <label for={defaultId}>Default augmentations:</label>
        <input
          type="radio"
          id={defaultId}
          name={radioName}
          checked={selectedAugmentations === null}
          onchange={() => {
            setAugmentations(null)
          }}
        />
      </div>
      <div class={radioContainerClass}>
        <label for={customId}>Custom augmentations:</label>
        <input
          type="radio"
          id={customId}
          name={radioName}
          checked={selectedAugmentations !== null}
          onchange={() => {
            setAugmentations([])
          }}
        />
      </div>
      {#if selectedAugmentations !== null}
        {@const augmentationState =
          selectedAugmentations.length !== 0
            ? 'Selected augmentations: ' +
              selectedAugmentations
                .map((augmentation) => displayAugmentationNames[augmentation])
                .join(', ')
            : 'No augmentation selected'}
        <div class="alert variant-outline-primary my-2">{augmentationState}</div>
        <div class={chipContainerClass} id={chipListId}>
          {#each availableAugmentations as augmentation (augmentation)}
            <button
              type="button"
              class="chip {(selectedAugmentations as string[]).includes(augmentation)
                ? 'variant-filled-primary'
                : 'variant-filled-surface'}"
              onclick={() => toggleSelectedAugmentation(augmentation)}
            >
              {displayAugmentationNames[augmentation]}
            </button>
          {/each}
        </div>
      {/if}
    {/if}
  </div>
{/snippet}

{#if fileImportMode}
  <form
    class="card relative h-full w-[48rem] max-w-[80vw] justify-self-center overflow-y-scroll px-6"
    class:dimmed={isLoading}
  >
    <h3 class="h3 sticky top-0 z-10 justify-between bg-surface-50 p-4 dark:bg-surface-800">
      Import dataset from file
      <CloseModalButton onClose={parent.onClose} />
    </h3>
    <FileDropzone name="fileImport" onchange={handleFileImport}>
      {#snippet lead()}
        <div class="flex w-full justify-center">
          <Icon icon="iconoir:upload-square-solid" class="h-12 w-12" />
        </div>
      {/snippet}
      {#snippet message()}
        <p>Drop files here or click to upload</p>
      {/snippet}
      {#snippet meta()}
        Please use the JSON file from the ZIP of a previous run's results
      {/snippet}
    </FileDropzone>
    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <div class="flex w-full items-center justify-between">
        <button
          class="variant-filled-surface btn"
          type="button"
          onclick={() => {
            fileImportMode = false
          }}
          disabled={isLoading}
        >
          Cancel import
        </button>
      </div>
    </footer>
  </form>
{:else if $modalStore[0]}
  <form
    class={`card relative h-[30rem] max-w-[80vw] justify-self-center overflow-y-scroll px-6 ${readOnly ? 'w-[38rem]' : 'w-[48rem]'}`}
    bind:this={datasetFormElement}
    class:dimmed={isLoading}
  >
    {#if isLoading}
      <div
        class="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-secondary-400 bg-opacity-20"
      >
        <ProgressRadial
          stroke={75}
          meter="stroke-surface-200"
          track="stroke-surface-200/30"
          strokeLinecap="round"
          indeterminate={true}
        />
      </div>
    {/if}
    {#if datasetInputValues.modality}
      <h3 class="h3 sticky top-0 z-10 justify-between bg-surface-50 py-4 dark:bg-surface-800">
        {$modalStore[0].title} ({displayModalityTypes[datasetInputValues.modality].toLowerCase()})
        <CloseModalButton onClose={parent.onClose} />
      </h3>
      <div class="m-1">{$modalStore[0].body}</div>

      <RequiredNameField
        id="name"
        label="Dataset name:"
        {invalids}
        field="name"
        bind:value={datasetInputValues.name}
        {readOnly}
      />

      <label for="organization-to-associate-with">Associated organization:</label>
      {#if readOnly}
        <ReadOnly
          value={datasetInputValues.organizationId
            ? $organizationsStore.get(datasetInputValues.organizationId)?.name
            : ''}
        />
      {:else if $organizationAsyncState.loading}
        <p>Loading organizations...</p>
      {:else if $organizationAsyncState.error}
        <p>{$organizationAsyncState.error}</p>
      {:else}
        <Select
          required
          id="organization-to-associate-with"
          name="organization-to-associate-with"
          bind:value={datasetInputValues.organizationId}
        >
          <option selected value>Select organization</option>
          {#each $organizationsStore as [_id, organization] (organization.id)}
            <option
              value={organization.id}
              selected={organization.id === datasetInputValues.organizationId}
            >
              {organization.name}
            </option>
          {/each}
        </Select>
      {/if}

      <label for="labeling-type">Labeling type:</label>
      {#if readOnly}
        <ReadOnly
          value={datasetInputValues.labelingType
            ? displayLabelingTypes[datasetInputValues.labelingType]
            : ''}
        />
      {:else}
        <Select
          name="labeling-type"
          placeholder="Select Type"
          required
          class={invalids.labelingType.invalid ? ' invalid' : ''}
          bind:value={datasetInputValues.labelingType}
        >
          <option selected value={null}>Select type</option>
          {#each labelingTypesValuesByModalityType[datasetInputValues.modality] as labelingType (labelingType)}
            <option value={labelingType}>{displayLabelingTypes[labelingType]}</option>
          {/each}
        </Select>
      {/if}

      <StorageConfigurator
        {readOnly}
        bind:this={storageConfigurator}
        bind:isEditMode
        bind:invalids
        bind:selectedStorageConfig
        bind:isAccordionOpen
        bind:inputValues={datasetInputValues}
        bind:storageInputValues
        bind:storageFormElement
        bind:storageConfigName
        bind:selectedStorageType
        bind:storageConfig
        creating="dataset"
      />

      <label for="metadataType">Metadata type:</label>
      {#if readOnly}
        <ReadOnly
          value={datasetInputValues.labelingInfo.type
            ? displayMetadataTypes[datasetInputValues.labelingInfo.type]
            : ''}
        />
      {:else}
        <Select
          name="metadataType"
          placeholder="Select Type"
          required
          value={datasetInputValues.labelingInfo.type}
          onchange={(e) => {
            switch (e.currentTarget.value) {
              case DatasetMetadataType.HirundoCSV:
                datasetInputValues.labelingInfo = new HirundoCSV()
                break
              case DatasetMetadataType.MultimodalHirundoCSV:
                datasetInputValues.labelingInfo = new MultimodalHirundoCSV()
                break
              case DatasetMetadataType.COCO:
                datasetInputValues.labelingInfo = new COCO()
                break
              case DatasetMetadataType.YOLO:
                datasetInputValues.labelingInfo = new YOLO()
                break
              case DatasetMetadataType.HuggingFaceAudio:
                datasetInputValues.labelingInfo = new HuggingFaceAudio()
                break
            }
          }}
        >
          <option selected value>Select type</option>
          {#each datasetMetadataOptions as metadataType (metadataType)}
            <option value={metadataType}>{displayMetadataTypes[metadataType]}</option>
          {/each}
        </Select>
      {/if}

      {#if isMultimodalHirundoCSV(datasetInputValues.labelingInfo)}
        <div class="space-y-3">
          <p class="font-semibold">Modality inputs</p>
          {#each datasetInputValues.labelingInfo.modalityCsvs as modalityCsv, index (index)}
            <div class="grid gap-3 rounded border border-surface-300 p-3 md:grid-cols-[10rem,1fr]">
              <div class="space-y-1">
                <label class="text-sm font-medium" for={`multimodal-modality-${index}`}>
                  Modality
                </label>
                <Select
                  name={`multimodal-modality-${index}`}
                  bind:value={modalityCsv.modality}
                  onchange={() => resetMultimodalModalityFields(modalityCsv)}
                  required
                >
                  {#each availableMultimodalModalityOptions as modalityOption (modalityOption)}
                    <option
                      value={modalityOption}
                      disabled={isMultimodalModalitySelectedElsewhere(modalityOption, index)}
                    >
                      {multimodalModalityName(modalityOption)}
                    </option>
                  {/each}
                </Select>
              </div>
              <div class="space-y-3">
                <div class="space-y-1">
                  <span class="flex items-center gap-1 text-sm font-medium">
                    {multimodalModalityName(modalityCsv.modality)} Hirundo CSV URL
                  </span>
                  <MetadataFields
                    prefix={dataPrefix}
                    {invalids}
                    fieldName="csvUrl"
                    fieldValue={modalityCsv.labelingInfo.csvUrl}
                    setField={(value: string) => {
                      modalityCsv.labelingInfo.csvUrl = value
                    }}
                    disabled={readOnly}
                    required
                  />
                </div>
                {#if isMultimodalVisionLikeModality(modalityCsv.modality)}
                  <div class="space-y-1">
                    <span class="flex items-center gap-1 text-sm font-medium">
                      {multimodalModalityName(modalityCsv.modality)} data root URL
                    </span>
                    <MetadataFields
                      prefix={dataPrefix}
                      {invalids}
                      fieldName="dataRootUrl"
                      fieldValue={modalityCsv.dataRootUrl ?? ''}
                      setField={(value: string) => {
                        modalityCsv.dataRootUrl = value || null
                      }}
                      disabled={readOnly}
                    />
                  </div>
                {/if}
                {#if isMultimodalTabularLikeModality(modalityCsv.modality)}
                  {@render multimodalChildTabularColumnSelection(modalityCsv, index)}
                {/if}
                <div class="space-y-2">
                  {@render augmentationSelection(
                    `${multimodalModalityName(modalityCsv.modality)} augmentations`,
                    modalityCsv.augmentations,
                    multimodalAugmentationValues(modalityCsv.modality),
                    (value) => {
                      modalityCsv.augmentations = value
                    },
                    (augmentation) => toggleMultimodalAugmentation(modalityCsv, augmentation),
                    `multimodal-augmentations-mode-${index}`,
                    `multimodal-augmentations-default-${index}`,
                    `multimodal-augmentations-custom-${index}`,
                    `multimodal-augmentations-${index}`,
                    'text-sm font-medium',
                    'flex flex-wrap items-center gap-x-4 gap-y-2',
                    'variant-ghost-surface flex flex-wrap items-center gap-2 py-2',
                  )}
                </div>
                {#if !readOnly && datasetInputValues.labelingInfo.modalityCsvs.length > 2}
                  <button
                    class="variant-filled-error btn w-fit"
                    type="button"
                    onclick={() => removeMultimodalCsv(index)}
                  >
                    Remove modality
                  </button>
                {/if}
              </div>
            </div>
          {/each}
          {#if !readOnly}
            <button
              class="variant-filled-secondary btn"
              type="button"
              onclick={addMultimodalCsv}
              disabled={!hasAvailableMultimodalModality}
            >
              Add modality
            </button>
          {/if}
          <div class="space-y-1">
            <span class="flex items-center gap-1 text-sm font-medium">
              Alignment CSV URL (optional)
              {@render fieldInfo(
                'Optional CSV with one {modality}_idx column per modality, such as image_idx, tabular_idx, or timeseries_idx, plus class_name. Leave empty when all modality CSVs have matching row order and row count.',
                'multimodal-alignment-csv-info',
              )}
            </span>
            <MetadataFields
              prefix={dataPrefix}
              {invalids}
              fieldName="csvUrl"
              fieldValue={datasetInputValues.labelingInfo.alignmentCsvUrl ?? ''}
              setField={(value: string) => {
                if (isMultimodalHirundoCSV(datasetInputValues.labelingInfo)) {
                  datasetInputValues.labelingInfo.alignmentCsvUrl = value || null
                }
              }}
              disabled={readOnly}
              required={false}
            />
          </div>
        </div>
      {/if}

      {#if modalityTypesWithData.includes(datasetInputValues.modality) && !isHuggingFaceAudio(datasetInputValues.labelingInfo)}
        {#if isYOLO(datasetInputValues.labelingInfo)}
          <label for="data-root-url">Images directory URL:</label>
        {:else}
          <label for="data-root-url">Data root URL:</label>
        {/if}
        {#if readOnly}
          <ReadOnly value={datasetInputValues.dataRootUrl} />
        {:else}
          <MetadataFields
            prefix={dataPrefix}
            {invalids}
            fieldName="dataRootUrl"
            fieldValue={datasetInputValues.dataRootUrl}
            setField={(value: string) => {
              datasetInputValues.dataRootUrl = value
            }}
            required={false}
          ></MetadataFields>
        {/if}
      {/if}
      {#if isHirundoCSV(datasetInputValues.labelingInfo)}
        <label for="csvUrl">CSV URL:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.csvUrl} />
        {:else}
          <MetadataFields
            prefix={dataPrefix}
            {invalids}
            fieldName="csvUrl"
            fieldValue={datasetInputValues.labelingInfo['csvUrl']}
            setField={(value: string) => {
              if (isHirundoCSV(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.csvUrl = value
                if (value.replace(dataPrefix, '')) {
                  invalids.csvUrl.setValid()
                } else {
                  invalids.csvUrl.setInvalid('CSV URL is required')
                }
              }
            }}
            required={false}
          ></MetadataFields>
        {/if}
        {#if isTabularLikeDataset}
          {@render tabularColumnSelection(
            'tabular-column-mode',
            'extra-non-feature-cols',
            'feature-cols',
            '',
          )}
        {/if}
      {:else if isCOCO(datasetInputValues.labelingInfo)}
        <label for="jsonUrl">JSON URL:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.jsonUrl} />
        {:else}
          <MetadataFields
            prefix={dataPrefix}
            {invalids}
            fieldName="jsonUrl"
            fieldValue={datasetInputValues.labelingInfo['jsonUrl']}
            setField={(value: string) => {
              if (isCOCO(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.jsonUrl = value
                if (value.replace(dataPrefix, '')) {
                  invalids.jsonUrl.setValid()
                } else {
                  invalids.jsonUrl.setInvalid('JSON URL is required')
                }
              }
            }}
            required={false}
          ></MetadataFields>
        {/if}
      {:else if isYOLO(datasetInputValues.labelingInfo)}
        <label for="labelsDirUrl">Labels directory URL:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.labelsDirUrl} />
        {:else}
          <MetadataFields
            prefix={dataPrefix}
            {invalids}
            fieldName="labelsDirUrl"
            fieldValue={datasetInputValues.labelingInfo['labelsDirUrl']}
            setField={(value: string) => {
              if (isYOLO(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.labelsDirUrl = value
                if (value.replace(dataPrefix, '')) {
                  invalids.labelsDirUrl.setValid()
                } else {
                  invalids.labelsDirUrl.setInvalid('Labels directory URL is required')
                }
              }
            }}
          ></MetadataFields>
        {/if}
      {:else if isHuggingFaceAudio(datasetInputValues.labelingInfo)}
        <label for="audioColumn">Audio Column:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.audioColumn} />
        {:else}
          <input
            class="input px-2 py-1"
            type="text"
            id="audioColumn"
            bind:value={datasetInputValues.labelingInfo.audioColumn}
          />
        {/if}
        <label for="textColumn">Text Column:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.textColumn} />
        {:else}
          <input
            class="input px-2 py-1"
            type="text"
            id="textColumn"
            bind:value={datasetInputValues.labelingInfo.textColumn}
          />
        {/if}
        <label for="configName">Config name:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.configName} />
        {:else}
          <input
            class="input px-2 py-1"
            type="text"
            id="configName"
            value={datasetInputValues.labelingInfo.configName}
            onchange={(event) => {
              if (
                isHuggingFaceAudio(datasetInputValues.labelingInfo) &&
                !!event.currentTarget.value
              ) {
                datasetInputValues.labelingInfo.configName = event.currentTarget.value
              } else if (isHuggingFaceAudio(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.configName = null
              }
            }}
          />
        {/if}
        <label for="dataDir">Data directory:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.dataDir} />
        {:else}
          <input
            class="input px-2 py-1"
            type="text"
            id="dataDir"
            value={datasetInputValues.labelingInfo.dataDir}
            onchange={(event) => {
              if (
                isHuggingFaceAudio(datasetInputValues.labelingInfo) &&
                !!event.currentTarget.value
              ) {
                datasetInputValues.labelingInfo.dataDir = event.currentTarget.value
              } else if (isHuggingFaceAudio(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.dataDir = null
              }
            }}
          />
        {/if}
        <label for="dataFiles">Data files:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.dataFiles} />
        {:else}
          <input
            class="input px-2 py-1"
            type="text"
            id="dataFiles"
            value={datasetInputValues.labelingInfo.dataFiles}
            onchange={(event) => {
              if (
                isHuggingFaceAudio(datasetInputValues.labelingInfo) &&
                !!event.currentTarget.value
              ) {
                datasetInputValues.labelingInfo.dataFiles = event.currentTarget.value
              } else if (isHuggingFaceAudio(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.dataFiles = null
              }
            }}
          />
        {/if}
        <label for="split">Split:</label>
        {#if readOnly}
          <ReadOnly value={datasetInputValues.labelingInfo.split} />
        {:else}
          <input
            class="input px-2 py-1"
            type="text"
            id="split"
            value={datasetInputValues.labelingInfo.split}
            onchange={(event) => {
              if (
                isHuggingFaceAudio(datasetInputValues.labelingInfo) &&
                !!event.currentTarget.value
              ) {
                datasetInputValues.labelingInfo.split = event.currentTarget.value
              } else if (isHuggingFaceAudio(datasetInputValues.labelingInfo)) {
                datasetInputValues.labelingInfo.split = null
              }
            }}
          />
        {/if}
      {/if}

      {#if datasetInputValues.labelingType === LabelingType.SpeechToText}
        <label for="language">Language:</label>
        {#if readOnly}
          <ReadOnly
            value={datasetInputValues.language
              ? displaySTTLanguages[datasetInputValues.language]
              : ''}
          />
        {:else}
          <Select
            name="language"
            placeholder="Select language"
            required
            bind:value={datasetInputValues.language}
          >
            <option selected value>Select language</option>
            {#each sttLanguages as language (language)}
              <option value={language}>{displaySTTLanguages[language]}</option>
            {/each}
          </Select>
        {/if}
      {:else}
        <label for="classes">Classes:</label>
        <div class="space-y-2">
          {#if readOnly}
            {#if selectedClassesOption === ClassesOption.AutoDetection && isYOLO(datasetInputValues.labelingInfo)}
              <label for="dataYamlUrl">From data YAML URL:</label>
              <ReadOnly value={datasetInputValues.labelingInfo.dataYamlUrl} />
            {:else if selectedClassesOption === ClassesOption.AutoDetection}
              <ReadOnly value="Auto-detected" />
            {:else}
              <label for="classes-chips">Manually input:</label>
              <ReadOnly value={datasetInputValues.classes.join(', ')} />
            {/if}
          {:else}
            <label class="flex items-center space-x-2">
              <input
                class="radio"
                type="radio"
                checked={selectedClassesOption === ClassesOption.AutoDetection}
                name="radio-direct"
                value="1"
                onchange={() => {
                  selectedClassesOption = ClassesOption.AutoDetection
                  datasetInputValues.classes = []
                }}
              />
              {#if isYOLO(datasetInputValues.labelingInfo)}
                <label for="dataYamlUrl">From data YAML URL:</label>
                <MetadataFields
                  prefix={dataPrefix}
                  {invalids}
                  fieldName="dataYamlUrl"
                  fieldValue={datasetInputValues.labelingInfo['dataYamlUrl']}
                  setField={(value: string) => {
                    if (isYOLO(datasetInputValues.labelingInfo)) {
                      if (value == '') {
                        datasetInputValues.labelingInfo.dataYamlUrl = null
                      } else {
                        datasetInputValues.labelingInfo.dataYamlUrl = value
                      }
                    }
                  }}
                  required={false}
                  disabled={selectedClassesOption === ClassesOption.ManualInput}
                ></MetadataFields>
              {:else}
                <p>Auto-detection</p>
              {/if}
            </label>
            <label class="flex items-center space-x-2">
              <input
                class="radio"
                type="radio"
                name="radio-direct"
                value="2"
                checked={selectedClassesOption === ClassesOption.ManualInput}
                onchange={() => (selectedClassesOption = ClassesOption.ManualInput)}
              />
              <label for="classes-chips">Manually input:</label>
              <InputChip
                bind:value={datasetInputValues.classes}
                name="classes-chips"
                placeholder="Type a class and hit Enter "
                disabled={selectedClassesOption === ClassesOption.AutoDetection}
                allowUpperCase
              />
            </label>
          {/if}
        </div>
      {/if}

      {#if datasetInputValues.labelingType !== LabelingType.SpeechToText && datasetInputValues.modality !== ModalityType.MULTIMODAL}
        {@render augmentationSelection(
          'Augmentations',
          datasetInputValues.augmentations,
          augmentationValues,
          (value) => {
            datasetInputValues.augmentations = value
          },
          toggleAugmentation,
          'augmentations-mode',
          'augmentations-default',
          'augmentations-custom',
          'augmentations',
        )}
      {/if}

      <footer
        class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
      >
        <div class="flex w-full items-center justify-between">
          {#if !readOnly}
            <div class="variant-filled-primary btn-group space-x-2">
              <button
                class="variant-filled-primary {parent.buttonPositive}"
                type="button"
                onclick={createOrUpdateHandler}
                disabled={isLoading}
              >
                {parent.buttonTextSubmit}
              </button>
              {#if datasetInputValues.modality}
                <button
                  class="variant-filled-error"
                  type="button"
                  onclick={() => {
                    delete datasetInputValues.modality
                  }}
                  disabled={isLoading}
                >
                  Back
                </button>
              {:else}
                <button
                  class="variant-filled-surface"
                  type="button"
                  onclick={parent.onClose}
                  disabled={isLoading}
                >
                  {parent.buttonTextCancel}
                </button>
              {/if}
            </div>
          {/if}
          {#if !$modalStore[0].meta.hideImport}
            <button
              class="variant-filled-secondary btn"
              type="button"
              onclick={importFromFiles}
              disabled={isLoading}
            >
              Import from file
            </button>
          {/if}
          {#if !isEditMode}
            <button
              class="variant-filled-primary btn"
              type="button"
              onclick={createOrUpdateAndRunHandler}
              disabled={isLoading}
            >
              <Icon icon="iconoir:play-solid" />
            </button>
          {/if}
        </div>
      </footer>
    {:else}
      <h3 class="h3 py-4">Choose a modality<CloseModalButton onClose={parent.onClose} /></h3>
      <div class="card variant-ghost-primary mt-5 space-y-2 px-4 py-4">
        <p>Before creating a new dataset, you need to choose a modality.</p>
        <p>
          The modality of the dataset determines the type of data it contains and the labeling type
          that can be applied to it.
        </p>
        <div class="flex flex-wrap justify-center gap-x-4 gap-y-3">
          {#each modalityTypes as modality (modality)}
            <button
              class="flex w-16 flex-col items-center gap-1.5"
              onclick={() => {
                datasetInputValues.modality = modality
                // Set to the first valid labeling type for the new modality
                datasetInputValues.labelingType = labelingTypesValuesByModalityType[modality][0]
                if (modality === ModalityType.MULTIMODAL) {
                  datasetInputValues.labelingInfo = new MultimodalHirundoCSV()
                  datasetInputValues.augmentations = null
                }
              }}
            >
              <div class="modality-icon-container variant-outline-primary btn-icon btn-icon-xl">
                {#if modality === ModalityType.RADAR}
                  <span class="modality-icon" aria-label="Radar"><NounModalityRadar /></span>
                {:else if modality === ModalityType.VISION}
                  <Icon
                    icon="iconoir:media-image"
                    class="modality-icon-svg"
                    width="2rem"
                    height="2rem"
                  />
                {:else if modality === ModalityType.SPEECH}
                  <Icon
                    icon="iconoir:voice-circle"
                    class="modality-icon-svg"
                    width="2rem"
                    height="2rem"
                  />
                {:else if modality === ModalityType.TABULAR}
                  <span class="modality-icon" aria-label="Tabular"><NounModalityData /></span>
                {:else if modality === ModalityType.MULTIMODAL}
                  <span class="modality-icon" aria-label="Multimodal">
                    <NounModalityMultimodal />
                  </span>
                {:else if modality === ModalityType.TIMESERIES}
                  <span class="modality-icon" aria-label="Time series"
                    ><NounModalityTimeseries /></span
                  >
                {/if}
              </div>
              <span class="text-center text-sm leading-tight">{displayModalityTypes[modality]}</span
              >
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </form>
{/if}

<style lang="postcss">
  form {
    flex-direction: column;
  }
  form > :not(:first-child) {
    margin: 0.5rem 0;
  }

  .disable-accordion {
    @apply pointer-events-none cursor-not-allowed opacity-50;
  }

  /* Modality icon container styling - set color for all icons */
  .modality-icon-container {
    @apply shrink-0 text-primary-500;
  }

  :global(.dark) .modality-icon-container {
    @apply text-white;
  }

  /* Consistent sizing for inline SVG modality icons */
  .modality-icon {
    @apply flex h-8 w-8 items-center justify-center;
  }

  .modality-icon :global(svg) {
    @apply h-full w-full;
  }

  /* Consistent sizing for iconoir modality icons */
  .modality-icon-container :global(.modality-icon-svg) {
    @apply h-8 w-8;
  }
</style>
